/**
 * Analytics Service
 * 
 * Comprehensive analytics tracking for user journey and interactions.
 * Tracks page views, user actions, journey progress, and engagement metrics.
 */

interface AnalyticsEvent {
  eventType: string;
  stepId?: string;
  data: Record<string, any>;
  timestamp?: Date;
}

interface PageViewData {
  path: string;
  title: string;
  referrer?: string;
  duration?: number;
}

interface UserAction {
  action: string;
  category: string;
  label?: string;
  value?: number;
  metadata?: Record<string, any>;
}

class AnalyticsService {
  private sessionId: string;
  private userId: string | null = null;
  private organizationId: string = 'default';
  private pageStartTime: number = Date.now();
  private isEnabled: boolean = true;
  private eventQueue: AnalyticsEvent[] = [];
  private flushInterval: NodeJS.Timeout | null = null;

  constructor() {
    // Generate or retrieve session ID
    this.sessionId = this.getOrCreateSessionId();
    
    // Start queue flushing
    this.startQueueFlushing();
    
    // Track page unload to send remaining events
    if (typeof window !== 'undefined') {
      window.addEventListener('beforeunload', () => this.flush());
      window.addEventListener('pagehide', () => this.flush());
    }
  }

  /**
   * Initialize analytics with user context
   */
  public initialize(userId: string, organizationId?: string) {
    this.userId = userId;
    if (organizationId) {
      this.organizationId = organizationId;
    }
    
    // Track session start
    this.trackEvent('session_start', {
      userId,
      organizationId: this.organizationId,
      userAgent: navigator.userAgent,
      screenResolution: `${window.screen.width}x${window.screen.height}`,
      language: navigator.language
    });
  }

  /**
   * Track page view
   */
  public trackPageView(path: string, title?: string) {
    const duration = Date.now() - this.pageStartTime;
    
    // Track time on previous page
    if (duration > 1000) { // Only track if spent more than 1 second
      this.trackEvent('page_time', {
        duration,
        path: window.location.pathname
      });
    }
    
    // Reset timer for new page
    this.pageStartTime = Date.now();
    
    // Track new page view
    const pageData: PageViewData = {
      path,
      title: title || document.title,
      referrer: document.referrer
    };
    
    this.trackEvent('page_view', pageData);
  }

  /**
   * Track user actions (clicks, form submissions, etc.)
   */
  public trackAction(action: string, category: string, label?: string, value?: number, metadata?: Record<string, any>) {
    const actionData: UserAction = {
      action,
      category,
      label,
      value,
      metadata
    };
    
    this.trackEvent('user_action', actionData);
  }

  /**
   * Track journey step start
   */
  public trackStepStart(stepId: string, stepName: string, metadata?: Record<string, any>) {
    this.trackEvent('step_start', {
      stepId,
      stepName,
      ...(metadata || {})
    }, stepId);
  }

  /**
   * Track journey step completion
   */
  public trackStepComplete(stepId: string, stepName: string, completionData?: Record<string, any>) {
    this.trackEvent('step_complete', {
      stepId,
      stepName,
      completionTime: Date.now() - this.pageStartTime,
      ...(completionData || {})
    }, stepId);
  }

  /**
   * Track story creation
   */
  public trackStoryCreated(storyId: string, storyData: Record<string, any>) {
    this.trackEvent('story_created', {
      storyId,
      ...(storyData || {})
    });
  }

  /**
   * Track simulation run
   */
  public trackSimulationRun(simulationType: string, results: Record<string, any>) {
    this.trackEvent('simulation_run', {
      simulationType,
      ...(results || {})
    });
  }

  /**
   * Track assessment submission
   */
  public trackAssessmentSubmit(assessmentType: string, score?: number, data?: Record<string, any>) {
    this.trackEvent('assessment_submit', {
      assessmentType,
      score,
      ...(data || {})
    });
  }

  /**
   * Track helicopter component progress
   */
  public trackComponentProgress(componentName: string, progress: number, metadata?: Record<string, any>) {
    this.trackEvent('component_progress', {
      componentName,
      progress,
      ...(metadata || {})
    });
  }

  /**
   * Track user engagement events
   */
  public trackEngagement(engagementType: string, data?: Record<string, any>) {
    this.trackEvent('engagement', {
      type: engagementType,
      ...(data || {})
    });
  }

  /**
   * Track errors and issues
   */
  public trackError(error: Error | string, context?: Record<string, any>) {
    this.trackEvent('error', {
      message: error instanceof Error ? error.message : error,
      stack: error instanceof Error ? error.stack : undefined,
      ...(context || {})
    });
  }

  /**
   * Track feature usage
   */
  public trackFeatureUsage(featureName: string, data?: Record<string, any>) {
    this.trackEvent('feature_usage', {
      feature: featureName,
      ...(data || {})
    });
  }

  /**
   * Core event tracking method
   */
  private trackEvent(eventType: string, data: Record<string, any>, stepId?: string) {
    if (!this.isEnabled) return;
    
    const event: AnalyticsEvent = {
      eventType,
      stepId,
      data: {
        ...(data || {}),
        url: window.location.href,
        timestamp: new Date().toISOString()
      }
    };
    
    // Add to queue
    this.eventQueue.push(event);
    
    // Flush if queue is getting large (increased threshold for batching)
    if (this.eventQueue.length >= 15) {
      this.flush();
    }
  }

  /**
   * Send events to backend in batches
   */
  private async flush() {
    if (this.eventQueue.length === 0) return;
    
    const events = [...this.eventQueue];
    this.eventQueue = [];
    
    // Prepare batch of events for single API call
    const batchData = events.map(event => ({
      userId: this.userId || 'anonymous',
      sessionId: this.sessionId,
      organizationId: this.organizationId,
      eventType: event.eventType,
      stepId: event.stepId || null,
      data: event.data,
      timestamp: event.timestamp || new Date()
    }));
    
    try {
      const response = await fetch('/api/analytics/events/batch', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({ events: batchData })
      });
      
      if (!response.ok) {
        console.error('Failed to send analytics batch:', response.statusText);
        // If batch fails, fall back to individual events for this session
        await this.fallbackToIndividualSend(events);
      }
    } catch (error) {
      console.error('Error sending analytics batch:', error);
      // If batch fails, fall back to individual events for this session  
      await this.fallbackToIndividualSend(events);
    }
  }

  /**
   * Fallback method to send events individually if batch fails
   */
  private async fallbackToIndividualSend(events: AnalyticsEvent[]) {
    for (const event of events.slice(0, 3)) { // Limit fallback to 3 most recent events
      try {
        const eventData = {
          userId: this.userId || 'anonymous',
          sessionId: this.sessionId,
          organizationId: this.organizationId,
          eventType: event.eventType,
          stepId: event.stepId || null,
          data: event.data,
          timestamp: event.timestamp || new Date()
        };
        
        const response = await fetch('/api/analytics/events', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          credentials: 'include',
          body: JSON.stringify(eventData)
        });
        
        if (!response.ok) {
          console.error('Failed to send analytics event:', response.statusText);
          // Don't re-add to queue to prevent infinite loops
        }
      } catch (error) {
        console.error('Error sending analytics event:', error);
      }
    }
  }

  /**
   * Start periodic queue flushing
   */
  private startQueueFlushing() {
    // Flush every 60 seconds (reduced frequency)
    this.flushInterval = setInterval(() => {
      this.flush();
    }, 60000);
  }

  /**
   * Get or create session ID
   */
  private getOrCreateSessionId(): string {
    const key = 'analytics_session_id';
    let sessionId = sessionStorage.getItem(key);
    
    if (!sessionId) {
      sessionId = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      sessionStorage.setItem(key, sessionId);
    }
    
    return sessionId;
  }

  /**
   * Enable/disable analytics
   */
  public setEnabled(enabled: boolean) {
    this.isEnabled = enabled;
    if (enabled) {
      this.flush();
    }
  }

  /**
   * Clean up
   */
  public destroy() {
    if (this.flushInterval) {
      clearInterval(this.flushInterval);
    }
    this.flush();
  }
}

// Export singleton instance
export const analytics = new AnalyticsService();

// Export for type usage
export type { AnalyticsEvent, PageViewData, UserAction };